import { useEffect, useState, DragEvent } from 'react';
import { GripIcon, Icon } from './icons';

interface Props {
  initialKey: string;
  initialValue: string | null;
  onCommit: (patch: { key?: string; value?: string | null }) => void;
  onDelete: () => void;
  dragHandleProps?: { draggable: true; onDragStart: (e: DragEvent) => void; onDragEnd: () => void };
  dragActive?: boolean;
  dragOver?: boolean;
}

export function AttributeRow({
  initialKey,
  initialValue,
  onCommit,
  onDelete,
  dragHandleProps,
  dragActive,
  dragOver,
}: Props) {
  const [key, setKey] = useState(initialKey);
  const [value, setValue] = useState(initialValue ?? '');
  const [keyErr, setKeyErr] = useState(false);

  useEffect(() => { setKey(initialKey); }, [initialKey]);
  useEffect(() => { setValue(initialValue ?? ''); }, [initialValue]);

  const commitKey = () => {
    const trimmed = key.trim();
    if (!trimmed) { setKeyErr(true); setKey(initialKey); setTimeout(() => setKeyErr(false), 350); return; }
    if (trimmed !== initialKey) onCommit({ key: trimmed });
  };

  const commitValue = () => {
    const next = value.trim() || null;
    if (next !== (initialValue ?? null)) onCommit({ value: next });
  };

  const cls = ['ts-attr'];
  if (dragActive) cls.push('ts-row--dragging');
  if (dragOver) cls.push('ts-row--drop-target');

  return (
    <div className={cls.join(' ')}>
      <span
        className="ts-grip"
        aria-label="Drag to reorder attribute"
        style={{ width: 20, height: 20 }}
        {...(dragHandleProps ?? {})}
      >
        <GripIcon size={12} />
      </span>
      <input
        className={'ts-attr-key' + (keyErr ? ' ts-err' : '')}
        value={key}
        placeholder="Attribute name"
        aria-label="Attribute key"
        onChange={(e) => setKey(e.target.value)}
        onBlur={commitKey}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); (e.target as HTMLInputElement).blur(); }
          if (e.key === 'Escape') { setKey(initialKey); (e.target as HTMLInputElement).blur(); }
        }}
      />
      <input
        className="ts-attr-val"
        value={value}
        placeholder="Value (optional)"
        aria-label="Attribute value"
        onChange={(e) => setValue(e.target.value)}
        onBlur={commitValue}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); (e.target as HTMLInputElement).blur(); }
          if (e.key === 'Escape') { setValue(initialValue ?? ''); (e.target as HTMLInputElement).blur(); }
        }}
      />
      <button type="button" className="ts-attr-del" aria-label="Delete attribute" onClick={onDelete}>
        <Icon name="x" size={12} />
      </button>
    </div>
  );
}
